<template>
<div class='risk-score-details-indicator'>
  <div class='risk-score-details-indicator__value' @click='goToIssues'>
    <div class='risk-score-details-indicator__value__score' :class='{ "missing-data": dataMissing }' :style="`--score-color: ${valueToResult.color};`">{{valueToResult.text}}</div>
    <div class='risk-score-details-indicator__value__percentile' v-if='!dataMissing' :style="`--score-percentile-color: ${valueToResult.color};`">{{value}}</div>
  </div>
  <div class='risk-score-details-indicator__description' :class='{ "missing-data": dataMissing }'>
    <div class='risk-score-details-indicator__description__title'>{{$t(title)}}</div>
    <div class='risk-score-details-indicator__description__subtitle'
       :class='{ "missing-data": dataMissing }'
    >
      {{$t(subtitle)}}
  </div>
  </div>
</div>
</template>

<script>
import { views } from '@/router/constants';
import { IssueTypeNames } from '@/components/CentralizedIssues/CentralizedIssuesDataView.vue';
import { SET_DOMAIN_FILTER } from '@/store/filters/actionTypes';

export default {
  name: 'RiskScoreIndicator',
  props: {
    dataMissing: {
      type: Boolean,
      required: false,
      default: false,
    },
    value: {
      type: Number,
      required: true,
      default: () => 0,
    },
    title: {
      type: String,
      required: true,
      default: () => '',
    },
    subtitle: {
      type: String,
      required: true,
      default: () => '',
    },
    letters: {
      type: Array,
      default() {
        return [
          {
            letter: 'a',
            color: '#29CE9C',
          },
          {
            letter: 'b',
            color: '#F0BF16',
          },
          {
            letter: 'c',
            color: '#FBA212',
          },
          {
            letter: 'd',
            color: '#FF7A03',
          },
          {
            letter: 'e',
            color: '#ED3D68',
          },
          {
            letter: 'f',
            color: '#E132A0',
          },
        ];
      },
    },
    domains: {
      type: Array,
      required: true,
      default: () => [],
    },
  },

  methods: {
    async goToIssues() {
      await this.$store.dispatch(SET_DOMAIN_FILTER, this.domains);
      await this.$router.push({ name: views.ISSUES, params: { issueTypes: this.issueTypes } });
    },
  },
  computed: {
    valueToResult() {
      let result = 'n/a';
      let color = '#A9B6C3';
      if (this.dataMissing) {
        return {
          text: result,
          color,
        };
      }
      let scoreParsed = parseFloat(this.value);
      const fixedSum = (100 - scoreParsed).toFixed(1);
      scoreParsed = parseFloat(fixedSum);
      const roundScore = Math.round(scoreParsed);
      if (roundScore >= 50 && roundScore <= 100) { // F
        result = this.letters[this.letters.length - 1].letter;
        color = this.letters[this.letters.length - 1].color;
      }
      if (roundScore >= 40 && roundScore <= 49) { // E
        result = this.letters[this.letters.length - 2].letter;
        color = this.letters[this.letters.length - 2].color;
      }
      if (roundScore >= 30 && roundScore <= 39) { // D
        result = this.letters[this.letters.length - 3].letter;
        color = this.letters[this.letters.length - 3].color;
      }
      if (roundScore >= 20 && roundScore <= 29) { // C
        result = this.letters[this.letters.length - 4].letter;
        color = this.letters[this.letters.length - 4].color;
      }
      if (roundScore >= 10 && roundScore <= 19) { // B
        result = this.letters[this.letters.length - 5].letter;
        color = this.letters[this.letters.length - 5].color;
      }
      if (roundScore >= 0 && roundScore <= 9) { // A //[CYN-1274]
        result = this.letters[0].letter;
        color = this.letters[0].color;
      }
      return {
        text: result,
        color,
      };
    },
    indicatorToType() {
      return {
        'risk.score.details.table.title.hijack': [IssueTypeNames.hijack],
        'risk.score.details.table.title.smtp': [IssueTypeNames.smtp],
        'risk.score.details.table.title.dns': [IssueTypeNames.dns],
        'risk.score.details.table.title.cloud': [IssueTypeNames.cloud, IssueTypeNames.bucket, IssueTypeNames.cloudDiscovery],
        'risk.score.details.table.title.dataleaks': [IssueTypeNames.dataleaks],
        'risk.score.details.table.title.employees': [IssueTypeNames.employees],
        'risk.score.details.table.title.waf': [IssueTypeNames.waf],
        'risk.score.details.table.title.tech': [IssueTypeNames.technologies],
        'risk.score.details.table.title.ports': [IssueTypeNames.port],
      };
    },
    issueTypes() {
      return this.indicatorToType[this.title];
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.risk-score-details-indicator {
  display: flex;
  flex-direction: row;
  align-items: center;

  &__value {
    display: flex;
    flex-direction: row;
    align-items: center;

    &:hover {
      cursor: pointer;
    }

    &__score {
      font-family: $font-family-black;
      font-size: $font-size-4xl + 0.125rem;
      text-transform: uppercase;
      color: var(--score-color);
      min-width: $space-10;
      text-align: center;
      margin-left: -$space-2;

      &.missing-data {
        width: 100%;
        margin-left: -$space-1;
        cursor: default;
      }
    }

    &__percentile {
      padding-left: $space-2;
      font-family: $font-family-black;
      font-size: $font-size-lg;
      text-transform: uppercase;
      color: var(--score-percentile-color);
      min-width: 65px;
    }
  }

  &__description {
    display: flex;
    flex-direction: column;
    padding-left: $space-5;

    &__title {
      color: var(--text-primary);
      display: flex;
      font-family: $font-family-semibold;
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: 17px;
    }

    &__subtitle {
      color: var(--text-primary);
      font-family: $font-family-regular;
      font-size: $font-size-sm;
      letter-spacing: 0;
      line-height: 15px;

      &.missing-data {
        color: var(--severity-critical);
      }
    }

    &.missing-data {
      padding-left: 54px;
    }
  }
}
</style>
